import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterIcon, EyeIcon, EditIcon, CheckIcon, XIcon, DownloadIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { DatePicker } from '../../../components/ui/DatePicker';
import { VoucherStatusBadge } from '../../../components/voucher/VoucherStatusBadge';
import { Voucher, VoucherFilters, VoucherType, VoucherStatus, TransactionMode } from '../../../types/voucher.types';
import { voucherService } from '../../../services/voucher.service';
export function AllVouchersList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<VoucherFilters>({});
  useEffect(() => {
    loadVouchers();
  }, [filters]);
  const loadVouchers = async () => {
    setLoading(true);
    try {
      const data = await voucherService.getVouchers(filters);
      setVouchers(data);
    } catch (error) {
      console.error('Failed to load vouchers:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleApprove = async (id: string) => {
    try {
      await voucherService.approveVoucher(id);
      loadVouchers();
    } catch (error: any) {
      alert(error.message);
    }
  };
  const handleReject = async (id: string) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await voucherService.rejectVoucher(id, reason);
        loadVouchers();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const handleExport = () => {
    // Export functionality would be implemented here
    alert('Export functionality coming soon');
  };
  const allVoucherTypes = Object.values(VoucherType).map(type => ({
    value: type,
    label: type
  }));
  const statusOptions = Object.values(VoucherStatus).map(status => ({
    value: status,
    label: status
  }));
  const transactionModeOptions = Object.values(TransactionMode).map(mode => ({
    value: mode,
    label: mode
  }));
  const mockBranches = [{
    value: 'branch1',
    label: 'Hargeisa Branch'
  }, {
    value: 'branch2',
    label: 'Mogadishu Branch'
  }, {
    value: 'branch3',
    label: 'Berbera Branch'
  }];
  const mockProjects = [{
    value: 'proj1',
    label: 'Infrastructure Project'
  }, {
    value: 'proj2',
    label: 'Education Initiative'
  }];
  const getVoucherTypeColor = (type: VoucherType): string => {
    const receiveTypes = [VoucherType.CRV, VoucherType.BRV, VoucherType.SDV_RECEIVE, VoucherType.INTEREST_RECEIVE, VoucherType.DIVIDEND_RECEIVE];
    const paymentTypes = [VoucherType.CPV, VoucherType.BPV, VoucherType.ADV, VoucherType.BDV, VoucherType.SDV_REFUND, VoucherType.INV];
    if (receiveTypes.includes(type)) return 'bg-green-100 text-green-800';
    if (paymentTypes.includes(type)) return 'bg-red-100 text-red-800';
    if (type === VoucherType.JOURNAL || type === VoucherType.COLLECTION_JOURNAL || type === VoucherType.INSTALLMENT || type === VoucherType.ADJUSTMENT_JOURNAL) return 'bg-purple-100 text-purple-800';
    if (type === VoucherType.CONTRA) return 'bg-blue-100 text-blue-800';
    if (type === VoucherType.FOUT) return 'bg-orange-100 text-orange-800';
    if (type === VoucherType.FIN) return 'bg-teal-100 text-teal-800';
    return 'bg-gray-100 text-gray-800';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1373">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1374">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1375">
          <div data-id="element-1376">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1377">
              All Vouchers List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1378">
              Combined view of all voucher types across the system
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1379">
            <Button variant="secondary" onClick={handleExport} data-id="element-1380">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1381" />
              Export
            </Button>
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-1382">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-1383" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-1384">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1385">
              Advanced Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1386">
              <Input label="Voucher Number" placeholder="Search by number..." value={filters.voucherNo || ''} onChange={e => setFilters({
            ...filters,
            voucherNo: e.target.value
          })} data-id="element-1387" />

              <Select label="Voucher Type" options={allVoucherTypes} value={filters.voucherType || ''} onChange={e => setFilters({
            ...filters,
            voucherType: e.target.value as VoucherType
          })} data-id="element-1388" />

              <Select label="Status" options={statusOptions} value={filters.status || ''} onChange={e => setFilters({
            ...filters,
            status: e.target.value as VoucherStatus
          })} data-id="element-1389" />

              <Select label="Transaction Mode" options={transactionModeOptions} value={filters.transactionMode || ''} onChange={e => setFilters({
            ...filters,
            transactionMode: e.target.value as TransactionMode
          })} data-id="element-1390" />

              <Select label="Branch" options={mockBranches} value={filters.branchId || ''} onChange={e => setFilters({
            ...filters,
            branchId: e.target.value
          })} data-id="element-1391" />

              <Select label="Project" options={mockProjects} value={filters.projectId || ''} onChange={e => setFilters({
            ...filters,
            projectId: e.target.value
          })} data-id="element-1392" />

              <DatePicker label="Date From" value={filters.dateFrom || ''} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-1393" />

              <DatePicker label="Date To" value={filters.dateTo || ''} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-1394" />

              <Input label="Min Amount" type="number" value={filters.minAmount || ''} onChange={e => setFilters({
            ...filters,
            minAmount: Number(e.target.value)
          })} data-id="element-1395" />

              <Input label="Max Amount" type="number" value={filters.maxAmount || ''} onChange={e => setFilters({
            ...filters,
            maxAmount: Number(e.target.value)
          })} data-id="element-1396" />

              <Input label="Party Name" placeholder="Search party..." value={filters.partyId || ''} onChange={e => setFilters({
            ...filters,
            partyId: e.target.value
          })} data-id="element-1397" />

              <Input label="Created By" placeholder="Search creator..." value={filters.createdBy || ''} onChange={e => setFilters({
            ...filters,
            createdBy: e.target.value
          })} data-id="element-1398" />
            </div>
            <div className="flex justify-between items-center pt-2" data-id="element-1399">
              <div className="text-sm text-gray-600" data-id="element-1400">
                Showing {vouchers.length} voucher
                {vouchers.length !== 1 ? 's' : ''}
              </div>
              <Button variant="secondary" size="sm" onClick={() => setFilters({})} data-id="element-1401">
                Clear All Filters
              </Button>
            </div>
          </div>}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1402">
          <div className="bg-white p-4 rounded-lg border" data-id="element-1403">
            <div className="text-sm text-gray-600" data-id="element-1404">Total Vouchers</div>
            <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1405">
              {vouchers.length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border" data-id="element-1406">
            <div className="text-sm text-gray-600" data-id="element-1407">Pending Approval</div>
            <div className="text-2xl font-bold text-orange-600 mt-1" data-id="element-1408">
              {vouchers.filter(v => v.status === VoucherStatus.COMPLETED).length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border" data-id="element-1409">
            <div className="text-sm text-gray-600" data-id="element-1410">Approved</div>
            <div className="text-2xl font-bold text-green-600 mt-1" data-id="element-1411">
              {vouchers.filter(v => v.status === VoucherStatus.APPROVED).length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border" data-id="element-1412">
            <div className="text-sm text-gray-600" data-id="element-1413">Total Amount</div>
            <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1414">
              ${vouchers.reduce((sum, v) => sum + v.amount, 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Voucher Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-1415">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1416">
              Loading vouchers...
            </div> : <div className="overflow-x-auto" data-id="element-1417">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1418">
                <thead className="bg-gray-50" data-id="element-1419">
                  <tr data-id="element-1420">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1421">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1422">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1423">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1424">
                      Party
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1425">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1426">
                      Mode
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1427">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1428">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1429">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1430">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1431">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1432">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1433">
                  {vouchers.length === 0 ? <tr data-id="element-1434">
                      <td colSpan={12} className="px-6 py-12 text-center text-gray-500" data-id="element-1435">
                        No vouchers found. Adjust your filters or create a new
                        voucher.
                      </td>
                    </tr> : vouchers.map(voucher => <tr key={voucher.id} className="hover:bg-gray-50" data-id="element-1436">
                        <td className="px-6 py-4" data-id="element-1437">
                          <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1438">
                            {voucher.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1439">
                          {new Date(voucher.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-1440">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getVoucherTypeColor(voucher.voucherType)}`} data-id="element-1441">
                            {voucher.voucherType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1442">
                          {voucher.partyName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1443">
                          {voucher.referenceNo || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1444">
                          {voucher.transactionMode}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-1445">
                          ${voucher.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1446">
                          {voucher.branchName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1447">
                          {voucher.projectName || '-'}
                        </td>
                        <td className="px-6 py-4" data-id="element-1448">
                          <VoucherStatusBadge status={voucher.status} data-id="element-1449" />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1450">
                          {voucher.createdBy}
                        </td>
                        <td className="px-6 py-4" data-id="element-1451">
                          <div className="flex items-center justify-center gap-2" data-id="element-1452">
                            <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-gray-600 hover:text-gray-900" title="View Details" data-id="element-1453">
                              <EyeIcon className="w-4 h-4" data-id="element-1454" />
                            </button>
                            {voucher.isEditable && <button onClick={() => navigate(`/voucher/${voucher.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-1455">
                                <EditIcon className="w-4 h-4" data-id="element-1456" />
                              </button>}
                            {voucher.status === VoucherStatus.COMPLETED && <>
                                <button onClick={() => handleApprove(voucher.id)} className="text-green-600 hover:text-green-800" title="Approve" data-id="element-1457">
                                  <CheckIcon className="w-4 h-4" data-id="element-1458" />
                                </button>
                                <button onClick={() => handleReject(voucher.id)} className="text-red-600 hover:text-red-800" title="Reject" data-id="element-1459">
                                  <XIcon className="w-4 h-4" data-id="element-1460" />
                                </button>
                              </>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}